import type { NpcBody } from './types'
import { normalizePaperItemId, PAPER_MINECRAFT_VERSION } from './materialCatalog'

export type EquipmentPart = 'head' | 'body' | 'leftArm' | 'rightArm' | 'leftLeg' | 'rightLeg'

export type EquipmentAttachment = {
  item: string
  part: EquipmentPart
  size: [number, number, number]
  position: [number, number, number]
  rotation: [number, number, number]
  color: number
}

export type EquipmentPreviewIssue = {
  item: string
  slot: string
  status: 'invalid' | 'unsupported'
  message: string
}

export type EquipmentPreview = {
  attachments: EquipmentAttachment[]
  issues: EquipmentPreviewIssue[]
}

const ARMOR_COLORS: Record<string, number> = {
  leather: 0x8b5a2b,
  chainmail: 0xa7adb3,
  iron: 0xd8d8d8,
  golden: 0xf7c843,
  diamond: 0x42d9cf,
  netherite: 0x443a3b,
  turtle: 0x3b9b63,
}

const TOOL_COLORS: Record<string, number> = {
  wooden: 0x8b663d,
  stone: 0x777777,
  golden: 0xf7c843,
  iron: 0xd8d8d8,
  diamond: 0x42d9cf,
  netherite: 0x443a3b,
}

const SLOT_ALIASES: Record<string, string> = {
  hand: 'main_hand', mainhand: 'main_hand', main_hand: 'main_hand',
  offhand: 'off_hand', off_hand: 'off_hand',
  head: 'head', helmet: 'head',
  body: 'chest', chest: 'chest', chestplate: 'chest',
  legs: 'legs', leggings: 'legs',
  feet: 'feet', boots: 'feet',
}

/**
 * Builds only silhouettes Studio can identify factually. It never invents a hash color or a
 * generic box for an unknown item. The Minecraft client remains authoritative for item models.
 */
export function equipmentPreview(body: Pick<NpcBody, 'equipment' | 'heldItem'>): EquipmentPreview {
  const attachments: EquipmentAttachment[] = []
  const issues: EquipmentPreviewIssue[] = []
  let hasMainHand = false
  for (const [authoredSlot, authoredItem] of Object.entries(body.equipment)) {
    if (!authoredItem) continue
    const slot = SLOT_ALIASES[authoredSlot.toLowerCase().replace(/-/g, '_')]
    const item = normalizePaperItemId(authoredItem)
    if (!slot) {
      issues.push({ item: authoredItem, slot: authoredSlot, status: 'invalid', message: `Invalid equipment slot ${authoredSlot}` })
      continue
    }
    if (!item) {
      issues.push({ item: authoredItem, slot, status: 'invalid', message: `Not an item Material in Paper ${PAPER_MINECRAFT_VERSION}` })
      continue
    }
    if (slot === 'main_hand') hasMainHand = true
    const rendered = slot === 'main_hand' || slot === 'off_hand'
      ? heldGeometry(item, slot === 'off_hand' ? 'leftArm' : 'rightArm')
      : armorGeometry(item, slot)
    if (rendered.length) attachments.push(...rendered)
    else issues.push({ item, slot, status: 'unsupported', message: 'Valid runtime item; Studio has no honest local silhouette for it' })
  }
  if (body.heldItem && !hasMainHand) {
    const item = normalizePaperItemId(body.heldItem)
    if (!item) {
      issues.push({ item: body.heldItem, slot: 'main_hand', status: 'invalid', message: `Not an item Material in Paper ${PAPER_MINECRAFT_VERSION}` })
    } else {
      const rendered = heldGeometry(item, 'rightArm')
      if (rendered.length) attachments.push(...rendered)
      else issues.push({ item, slot: 'main_hand', status: 'unsupported', message: 'Valid runtime item; Studio has no honest local silhouette for it' })
    }
  }
  return { attachments, issues }
}

export function equipmentGeometry(body: Pick<NpcBody, 'equipment' | 'heldItem'>): EquipmentAttachment[] {
  return equipmentPreview(body).attachments
}

function armorGeometry(item: string, slot: string): EquipmentAttachment[] {
  const path = item.replace('minecraft:', '')
  const material = Object.keys(ARMOR_COLORS).find((value) => path === `${value}_${armorSuffix(slot)}`)
  if (!material) return []
  const color = ARMOR_COLORS[material]
  if (slot === 'head') return [box(item, 'head', [9, 9, 9], [0, 0, 0], color)]
  if (slot === 'chest') return [
    box(item, 'body', [9, 12.5, 5], [0, 0, 0], color),
    box(item, 'leftArm', [4.8, 5, 4.8], [0, 2.5, 0], color),
    box(item, 'rightArm', [4.8, 5, 4.8], [0, 2.5, 0], color),
  ]
  if (slot === 'legs') return [
    box(item, 'body', [8.5, 5, 4.8], [0, -3.8, 0], color),
    box(item, 'leftLeg', [4.8, 8, 4.8], [0, 2, 0], color),
    box(item, 'rightLeg', [4.8, 8, 4.8], [0, 2, 0], color),
  ]
  if (slot === 'feet') return [
    box(item, 'leftLeg', [4.8, 5, 5.8], [0, -4, -0.4], color),
    box(item, 'rightLeg', [4.8, 5, 5.8], [0, -4, -0.4], color),
  ]
  return []
}

function armorSuffix(slot: string): string {
  return slot === 'head' ? 'helmet' : slot === 'chest' ? 'chestplate' : slot === 'legs' ? 'leggings' : 'boots'
}

function heldGeometry(item: string, part: 'leftArm' | 'rightArm'): EquipmentAttachment[] {
  const path = item.replace('minecraft:', '')
  const toolMaterial = Object.keys(TOOL_COLORS).find((value) => path.startsWith(`${value}_`))
  const toolColor = toolMaterial ? TOOL_COLORS[toolMaterial] : 0
  const handle = () => box(item, part, [1.25, 8, 1.25], [0, -7, -1], 0x6f4a2c, [.28, 0, -.35])
  if (/_sword$/.test(path) && toolColor) return [
    handle(),
    box(item, part, [1.4, 8, .7], [-2.2, -12.5, -2.4], toolColor, [.28, 0, -.35]),
    box(item, part, [4, .8, 1], [-.5, -8.4, -1.4], 0x9a6a32, [.28, 0, -.35]),
  ]
  if (/_pickaxe$/.test(path) && toolColor) return [handle(), box(item, part, [6, 1.4, 1.4], [-1.3, -11, -2], toolColor, [.28, 0, -.35])]
  if (/_axe$/.test(path) && toolColor) return [handle(), box(item, part, [4, 3.5, 1.3], [-2, -10.4, -2], toolColor, [.28, 0, -.35])]
  if (/_shovel$/.test(path) && toolColor) return [handle(), box(item, part, [3.2, 3.8, 1.1], [-1.6, -11.4, -2.1], toolColor, [.28, 0, -.35])]
  if (/_hoe$/.test(path) && toolColor) return [handle(), box(item, part, [4.4, 1.2, 1.2], [-1.7, -10.8, -2], toolColor, [.28, 0, -.35])]
  if (path === 'mace') return [handle(), box(item, part, [3.8, 3.8, 3.8], [-1.8, -11.2, -2.2], 0x7790a0, [.28, 0, -.35])]
  if (path === 'trident') return [
    box(item, part, [1, 12, 1], [0, -9, -1], 0x4da9a7, [.28, 0, -.35]),
    box(item, part, [4.5, 1, 1], [-1.6, -15, -2.7], 0x8bd6c8, [.28, 0, -.35]),
  ]
  if (path === 'shield') return [box(item, part, [7, 10, 1], [0, -7, -2.5], 0x8b663d, [.12, 0, -.18])]
  if (path === 'bow') return [
    box(item, part, [1, 10, 1], [-1.5, -8, -1.5], 0x8b663d, [.28, 0, -.55]),
    box(item, part, [1, 10, 1], [1.5, -8, -1.5], 0x8b663d, [.28, 0, -.15]),
  ]
  if (path === 'crossbow') return [handle(), box(item, part, [8, 1.5, 1.5], [-1.4, -10, -2], 0x8b663d, [.28, 0, -.35])]
  return []
}

function box(item: string, part: EquipmentPart, size: [number, number, number],
             position: [number, number, number], color: number,
             rotation: [number, number, number] = [0, 0, 0]): EquipmentAttachment {
  return { item, part, size, position, rotation, color }
}
